import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { AzureFunctionLinkedService, SupportedAuthTypes } from 'app/model/linkedservice/azure-function.linkedservice';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel, passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { AuthenticationType, Credential, CredentialTypeOptions, ITridentResource } from 'shell-models-lib';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureFunctionLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                functionAppUrl: '*',
                functionKey: 'secret',
                encryptedCredential: '*',
                credential: '*',
                resourceId: '*',
                authentication: '*',
                ...(GlobalVariables.enableSpnAuthInAzFuncActivity ? {
                    servicePrincipalId: '*',
                    servicePrincipalKey: 'secret',
                    tenant: '*'
                } : {})
            },
            parameters: '*'
        }
    }
})
export class AzureFunctionLinkedServiceAdapter extends LinkedServiceAdapter<AzureFunctionLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureFunctionLinkedService): Promise<void> {
        let finalAuthRefType: boolean = false;
        let finalAuthType: string = model.authentication;

        if (model.credential) {
            finalAuthRefType = true;
            if (GlobalVariables.enableUamiAuthInAzFuncActivity) {
                const credType = model.credential.getType();
                if (credType === CredentialTypeOptions.ServicePrincipal) {
                    finalAuthType = AuthenticationType.ServicePrincipal;
                } else if (credType === CredentialTypeOptions.ManagedIdentity) {
                    finalAuthType = AuthenticationType.ManagedServiceIdentity;
                } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                    finalAuthType = AuthenticationType.UserAssignedManagedIdentity;
                } else {
                    finalAuthType = model.authentication;
                }
            } else {
                finalAuthType = AuthenticationType.ManagedServiceIdentity;
            }
        }
        model.isAuthRefMethodCredential = finalAuthRefType;
        model.authentication = finalAuthType;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Partial<Model.AzureFunctionLinkedServiceTypeProperties> => {
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(tridentResource.datasourceReference);
        const pathProperties = datasourceReference.path.split(';');
        return {
            functionAppUrl: pathProperties[0]
        };
    };

    protected modelConstructor(): new () => AzureFunctionLinkedService {
        return AzureFunctionLinkedService;
    }

    protected model(): IModel {
        return AzureFunctionLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureFunctionLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureFunctionLinkedService;

        properties.type = Metamodel.modelFor(AzureFunctionLinkedService).name;

        const functionKey: string | Model.SecretBase = parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureFunctionLinkedService.FUNCTION_KEY, this.context);
        const functionAppUrl: string = this.valueToResource(linkedService, AzureFunctionLinkedService.FUNCTION_APP_URL);

        properties.typeProperties = {
            functionAppUrl,
            authentication: linkedService.authentication
        };

        if (!linkedService.authentication) {
            properties.typeProperties.authentication = AuthenticationType.Anonymous;
        }

        if (GlobalVariables.enableSpnAuthInAzFuncActivity && linkedService.authentication === AuthenticationType.ServicePrincipal) {
            properties.typeProperties.servicePrincipalId = linkedService.servicePrincipalId;
            properties.typeProperties.servicePrincipalKey = passwordOrAKVtoSecretBase(linkedService.servicePrincipalKey, this.context);
            properties.typeProperties.tenant = linkedService.tenant;
            properties.typeProperties.resourceId = this.valueToResource(linkedService, AzureFunctionLinkedService.RESOURCEID);
        }

        if (linkedService.authentication === AuthenticationType.ManagedServiceIdentity || 
            (GlobalVariables.enableUamiAuthInAzFuncActivity && linkedService.authentication === AuthenticationType.UserAssignedManagedIdentity)) {
            properties.typeProperties.authentication = SupportedAuthTypes.MSI;
            properties.typeProperties.resourceId = this.valueToResource(linkedService, AzureFunctionLinkedService.RESOURCEID);

            if (linkedService.authentication === AuthenticationType.UserAssignedManagedIdentity && linkedService.credential) {
                properties.typeProperties.credential =
                    ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference;
            }

            if (!GlobalVariables.updateAzFuncSAMI){
                properties.typeProperties.functionKey = functionKey;
            }
        } else {
            properties.typeProperties.functionKey = functionKey;
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureFunctionLinkedService): Promise<AzureFunctionLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureFunctionLinkedService;
        const typeProperties: Model.AzureFunctionLinkedServiceTypeProperties = properties.typeProperties;

        if (typeProperties.functionAppUrl != null) {
            linkedService.accountNameSelectionMethod = 'manual';
        }

        linkedService.credential = undefined;
        ValueAdapter.toModel(linkedService, AzureFunctionLinkedService.FUNCTION_APP_URL, typeProperties.functionAppUrl);

        linkedService.authentication = typeProperties.authentication;

        if (GlobalVariables.enableSpnAuthInAzFuncActivity) {
            if (typeProperties.authentication === AuthenticationType.ServicePrincipal) {
                linkedService.servicePrincipalId = properties.typeProperties.servicePrincipalId;
                linkedService.servicePrincipalKey = secretBaseToPasswordOrAKV(properties.typeProperties.servicePrincipalKey, true, this.context);
                linkedService.tenant = properties.typeProperties.tenant;
                if (typeProperties.resourceId) {
                    ValueAdapter.toModel(linkedService, AzureFunctionLinkedService.RESOURCEID, typeProperties.resourceId);
                }
            } else {
                linkedService.servicePrincipalId = undefined;
                linkedService.servicePrincipalKey = undefined;
                linkedService.tenant = undefined;
            } 
        }

        if (!typeProperties.authentication) {
            linkedService.authentication = AuthenticationType.Anonymous;
        }

        if (typeProperties.authentication === SupportedAuthTypes.MSI) {
            // set correct auth method in postToModel call if credential reference is present.
            // NOTE anredd - we do this below line since 'MSI' is not technically valid option on the front end
            linkedService.authentication = AuthenticationType.ManagedServiceIdentity;

            if (typeProperties.resourceId) {
                ValueAdapter.toModel(linkedService, AzureFunctionLinkedService.RESOURCEID, typeProperties.resourceId);
            }

            if (typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential),
                    typeProperties.credential, this.context);
                linkedService.isAuthRefMethodCredential = true;
            }

            if (!GlobalVariables.updateAzFuncSAMI){
                parameterizableSecretBaseToModel(linkedService, AzureFunctionLinkedService.FUNCTION_KEY, typeProperties.functionKey, this.context);
            }
        } else {
            parameterizableSecretBaseToModel(linkedService, AzureFunctionLinkedService.FUNCTION_KEY, typeProperties.functionKey, this.context);
        }
        return linkedService;
    }
}
